// Phase MHM-1 · Hub orders page — extends the legacy `MainHubOrders`.
// All orders scoped to the caller's hub via `/api/hub/orders`.
// MHM-5.1 — Consumes `?filter=` / `?date=` from URL (set by the dashboard
//           KPI cards) and shows a clearable filter chip.
import { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Search, Download, RefreshCw, ChevronDown, ChevronRight, X as IconX } from "lucide-react";
import { toast } from "sonner";
import api from "../../services/api";
import MHMSubNav from "../../components/hub/MHMSubNav";
import TrackingIdLink from "../../components/hub/TrackingIdLink";
import RoleGate from "../../components/RoleGate";

const STATUSES = [
  "pickup_pending", "picked_up", "at_micro_hub", "at_main_hub",
  "dispatched", "in_transit", "out_for_delivery", "delivered", "rto", "failed",
];
const STATUS_COLORS = {
  pickup_pending: "bg-amber-100 text-amber-800",
  picked_up: "bg-violet-100 text-violet-800",
  at_micro_hub: "bg-indigo-100 text-indigo-800",
  at_main_hub: "bg-blue-100 text-blue-800",
  dispatched: "bg-cyan-100 text-cyan-800",
  in_transit: "bg-sky-100 text-sky-800",
  out_for_delivery: "bg-orange-100 text-orange-800",
  delivered: "bg-green-100 text-green-800",
  rto: "bg-rose-100 text-rose-800",
  failed: "bg-red-100 text-red-800",
};

export default function MainHubOrders() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [microHub, setMicroHub] = useState("");
  const [microHubs, setMicroHubs] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  // MHM-5.1 · KPI deep-link filter
  const [deepFilter, setDeepFilter] = useState(null);

  // Read the KPI-deep-link params on mount + whenever the URL changes.
  useEffect(() => {
    const f = searchParams.get("filter");
    const d = searchParams.get("date");
    const todayISO = new Date().toISOString().slice(0, 10);
    if (!f) { setDeepFilter(null); return; }
    let nextStatus = ""; let nextFrom = ""; let nextTo = "";
    let label = "";
    if (f === "today") {
      nextFrom = todayISO; nextTo = todayISO; label = "Created today";
    } else if (f === "delivered") {
      nextStatus = "delivered";
      if (d === "today") { nextFrom = todayISO; nextTo = todayISO; label = "Delivered today"; }
      else label = "Delivered";
    } else if (f === "delayed") {
      label = "Delayed (past SLA · 6 h+)";
    } else if (f === "pickup_pending") {
      nextStatus = "pickup_pending"; label = "Pending pickups";
    } else if (f === "failed") {
      nextStatus = "failed"; label = "Failed deliveries";
    } else {
      label = f;
    }
    setStatus(nextStatus); setDateFrom(nextFrom); setDateTo(nextTo); setPage(1);
    setDeepFilter({ key: f, label });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const clearDeepFilter = () => {
    setSearchParams({}, { replace: true });
    setStatus(""); setDateFrom(""); setDateTo(""); setPage(1);
    setDeepFilter(null);
  };

  const isDelayed = useMemo(() => deepFilter?.key === "delayed", [deepFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
      if (status) params.set("status", status);
      if (q) params.set("q", q);
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (microHub) params.set("micro_hub", microHub);
      if (isDelayed) params.set("delayed", "true");
      const { data } = await api.get(`/hub/orders?${params}`);
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e.userMessage || "Couldn't load orders");
    } finally { setLoading(false); }
  }, [page, pageSize, status, q, dateFrom, dateTo, microHub, isDelayed]);

  const loadMicroHubs = useCallback(async () => {
    try {
      const { data } = await api.get("/hub/micro-hubs");
      setMicroHubs(data.rows || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadMicroHubs(); }, [loadMicroHubs]);

  const exportCSV = async () => {
    try {
      const res = await api.post("/hub/orders/export", {
        filters: { status, date_from: dateFrom, date_to: dateTo },
        format: "csv",
      }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "hub_orders.csv"; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) {
      toast.error(e.userMessage || "Export failed");
    }
  };

  const toggleExpand = async (tid) => {
    if (expanded === tid) { setExpanded(null); setDrawer(null); return; }
    setExpanded(tid);
    try {
      const { data } = await api.get(`/hub/orders/${tid}`);
      setDrawer(data);
    } catch (e) {
      toast.error(e.userMessage || "Couldn't load detail");
      setDrawer(null);
    }
  };

  const toggleSelect = (tid) => {
    const next = new Set(selected);
    if (next.has(tid)) next.delete(tid); else next.add(tid);
    setSelected(next);
  };

  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <RoleGate allow={["main_hub_manager", "admin", "founder", "ceo"]}>
      <MHMSubNav />
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid="mhm-orders-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Outfit']" data-testid="mhm-orders-title">
              Hub Orders
            </h1>
            <p className="text-sm text-slate-500">{total} orders in your hub scope</p>
          </div>
          <div className="flex items-center gap-2">
            {deepFilter && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold
                               bg-orange-50 text-[#C2410C] border border-orange-200 rounded-full"
                    data-testid="mhm-orders-deeplink-chip">
                {deepFilter.label}
                <button onClick={clearDeepFilter}
                        data-testid="mhm-orders-deeplink-clear"
                        title="Clear KPI filter"
                        className="hover:bg-orange-100 rounded-full p-0.5">
                  <IconX className="h-3 w-3"/>
                </button>
              </span>
            )}
            <button
              onClick={load}
              data-testid="mhm-orders-refresh"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 hover:bg-slate-50 rounded-lg"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Refresh
            </button>
            <button
              onClick={exportCSV}
              data-testid="mhm-orders-export-csv"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-violet-600 hover:bg-violet-700 text-white rounded-lg"
            >
              <Download className="h-4 w-4" /> Export CSV
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              data-testid="mhm-orders-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (setPage(1), load())}
              placeholder="Search tracking, customer, phone…"
              className="pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-lg w-72"
            />
          </div>
          <select
            data-testid="mhm-orders-status-filter"
            value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            className="text-sm border border-slate-200 rounded-lg px-2 py-1.5"
          >
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select
            data-testid="mhm-orders-microhub-filter"
            value={microHub}
            onChange={(e) => { setMicroHub(e.target.value); setPage(1); }}
            className="text-sm border border-slate-200 rounded-lg px-2 py-1.5"
          >
            <option value="">All micro hubs</option>
            {microHubs.map((mh) => (
              <option key={mh.hub_id} value={mh.hub_id}>{mh.name || mh.hub_id}</option>
            ))}
          </select>
          <input
            data-testid="mhm-orders-date-from"
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
            className="text-sm border border-slate-200 rounded-lg px-2 py-1.5"
          />
          <input
            data-testid="mhm-orders-date-to"
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
            className="text-sm border border-slate-200 rounded-lg px-2 py-1.5"
          />
        </div>

        {/* Bulk toolbar */}
        {selected.size > 0 && (
          <div className="bg-violet-50 border border-violet-200 rounded-xl p-3 flex items-center gap-2 flex-wrap" data-testid="mhm-orders-bulk-toolbar">
            <span className="text-sm font-semibold text-violet-800">{selected.size} selected</span>
            <button
              disabled
              title="Bulk dispatch ships in MHM-2"
              className="text-xs px-2.5 py-1 rounded-full bg-white border border-violet-200 text-slate-400 cursor-not-allowed"
              data-testid="mhm-orders-bulk-dispatch"
            >Bulk dispatch (MHM-2)</button>
            <button
              onClick={exportCSV}
              data-testid="mhm-orders-bulk-export-csv"
              className="text-xs px-2.5 py-1 rounded-full bg-white border border-violet-200 hover:bg-violet-100"
            >Export selected CSV</button>
            <button
              onClick={() => setSelected(new Set())}
              className="text-xs text-violet-700 underline ml-auto"
              data-testid="mhm-orders-bulk-clear"
            >Clear</button>
          </div>
        )}

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="mhm-orders-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2 w-8"></th>
                <th className="px-3 py-2 w-8"></th>
                <th className="px-3 py-2 text-left">Tracking</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Pickup → Drop</th>
                <th className="px-3 py-2 text-left">Amount</th>
                <th className="px-3 py-2 text-left">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 && !loading && (
                <tr><td colSpan={7} className="text-center py-10 text-slate-400" data-testid="mhm-orders-empty">No orders found</td></tr>
              )}
              {rows.map((o) => (
                <>
                  <tr key={o.tracking_id} className="hover:bg-slate-50" data-testid={`mhm-orders-row-${o.tracking_id}`}>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selected.has(o.tracking_id)}
                        onChange={() => toggleSelect(o.tracking_id)}
                        data-testid={`mhm-orders-row-select-${o.tracking_id}`}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => toggleExpand(o.tracking_id)}
                        data-testid={`mhm-orders-row-expand-${o.tracking_id}`}
                        className="text-slate-400 hover:text-slate-700"
                      >
                        {expanded === o.tracking_id
                          ? <ChevronDown className="h-4 w-4" />
                          : <ChevronRight className="h-4 w-4" />}
                      </button>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs"><TrackingIdLink tid={o.tracking_id} /></td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${STATUS_COLORS[o.ui_status] || "bg-slate-100 text-slate-700"}`}>
                        {o.ui_status || o.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600">
                      {o.pickup_city || "—"} → {o.drop_city || "—"}
                    </td>
                    <td className="px-3 py-2">{o.final_price ? `₹${o.final_price}` : "—"}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      {(o.created_at || "").slice(0, 16).replace("T", " ")}
                    </td>
                  </tr>
                  {expanded === o.tracking_id && (
                    <tr key={`${o.tracking_id}-drawer`}>
                      <td colSpan={7} className="bg-slate-50 px-6 py-4">
                        {!drawer
                          ? <div className="text-sm text-slate-400">Loading details…</div>
                          : (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                              <div>
                                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Customer</div>
                                <div className="text-slate-700">{drawer.order.customer_name || drawer.order.sender_name || "—"}</div>
                                <div className="text-xs text-slate-500">{drawer.order.customer_phone_masked || drawer.order.recipient_phone_masked || "—"}</div>
                                <div className="text-xs text-slate-500 mt-1">{drawer.order.pickup_address || "—"}</div>
                              </div>
                              <div>
                                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Timeline</div>
                                <ul className="space-y-0.5 text-xs">
                                  {(drawer.timeline || []).slice(-6).map((t, i) => (
                                    <li key={i}>
                                      <span className="font-semibold">{t.status}</span> — {(t.at || "").slice(0, 16).replace("T", " ")}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              <div>
                                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Proofs & OTP</div>
                                <div className="text-xs">Proofs: {(drawer.proofs || []).length}</div>
                                <div className="text-xs">OTP log entries: {(drawer.otp_logs || []).length}</div>
                                {(drawer.order.items || []).length > 0 && (
                                  <div className="mt-2 text-xs text-slate-600">
                                    {drawer.order.items.length} parcel(s) · total {drawer.order.items.reduce((s, i) => s + (i.weight_kg || 0), 0).toFixed(2)} kg
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between text-sm" data-testid="mhm-orders-pagination">
          <span className="text-slate-500">Page {page} of {pages} · {total} total</span>
          <div className="flex gap-1">
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1.5 border border-slate-200 rounded disabled:opacity-40"
              data-testid="mhm-orders-page-prev"
            >Previous</button>
            <button
              disabled={page >= pages}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1.5 border border-slate-200 rounded disabled:opacity-40"
              data-testid="mhm-orders-page-next"
            >Next</button>
          </div>
        </div>
      </div>
    </RoleGate>
  );
}
